import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Popconfirm, message, Space, Alert, Collapse, Card, Row, Col, Statistic, Grid, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, WarningOutlined, FilterOutlined, ClearOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { islemlerAPI, kasalarAPI } from '../services/api';
import { formatPara, paraInputProps } from '../utils/format';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';
import dayjs from 'dayjs';

function Virman() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState([]);
  const [kasalar, setKasalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [onayModalOpen, setOnayModalOpen] = useState(false);
  const [bekleyenPayload, setBekleyenPayload] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreCikisKasaIds, setFiltreCikisKasaIds] = useState([]);
  const [filtreGirisKasaIds, setFiltreGirisKasaIds] = useState([]);
  const [filtreTutarOp, setFiltreTutarOp] = useState(null);
  const [filtreTutarDeger, setFiltreTutarDeger] = useState(null);
  const [filtreTarihBaslangic, setFiltreTarihBaslangic] = useState(null);
  const [filtreTarihBitis, setFiltreTarihBitis] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [virmanlarRes, kasalarRes] = await Promise.all([
        islemlerAPI.getVirmanlar(),
        kasalarAPI.getAll(),
      ]);
      setData(virmanlarRes.data);
      setKasalar(kasalarRes.data.filter(k => k.aktif));
    } catch (err) {
      message.error('Virmanlar yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!modalOpen) return;
    if (editingRecord) {
      form.setFieldsValue({
        tarih: editingRecord.tarih ? dayjs(editingRecord.tarih) : null,
        giris_kasa_id: editingRecord.giris_kasa_id || null,
        cikis_kasa_id: editingRecord.cikis_kasa_id || null,
        tutar: editingRecord.tutar,
        aciklama: editingRecord.aciklama,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ tarih: dayjs() });
    }
  }, [modalOpen, editingRecord, form]);

  const handleSubmit = (values) => {
    const giris = values.giris_kasa_id;
    const cikis = values.cikis_kasa_id;

    if (!giris && !cikis) {
      message.error('En az bir kasa seçmelisiniz');
      return;
    }

    const payload = {
      giris_kasa_id: giris || null,
      cikis_kasa_id: cikis || null,
      tarih: values.tarih?.format('YYYY-MM-DD'),
      tutar: values.tutar,
      aciklama: values.aciklama,
    };

    // Tek kasa seçildiyse onay iste
    if (!giris || !cikis) {
      setBekleyenPayload(payload);
      setOnayModalOpen(true);
    } else {
      virmanKaydet(payload);
    }
  };

  const virmanKaydet = async (payload) => {
    const guncelle = !!editingRecord;
    try {
      if (guncelle) {
        await islemlerAPI.updateVirman(editingRecord.id, payload);
        message.success('Virman güncellendi');
      } else {
        await islemlerAPI.createVirman(payload);
        message.success('Virman oluşturuldu');
      }
      setModalOpen(false);
      setEditingRecord(null);
      setOnayModalOpen(false);
      setBekleyenPayload(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((guncelle ? 'Virman güncellenemedi: ' : 'Virman oluşturulamadı: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await islemlerAPI.removeVirman(id);
      message.success('Virman silindi');
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const cikisKasaFilterOptions = useMemo(() => {
    const map = new Map();
    data.forEach(d => { if (d.cikis_kasa_id && d.cikis_kasa) map.set(d.cikis_kasa_id, d.cikis_kasa); });
    const list = Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    return bosSecenekliOptions(list, data, 'cikis_kasa_id');
  }, [data]);

  const girisKasaFilterOptions = useMemo(() => {
    const map = new Map();
    data.forEach(d => { if (d.giris_kasa_id && d.giris_kasa) map.set(d.giris_kasa_id, d.giris_kasa); });
    const list = Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    return bosSecenekliOptions(list, data, 'giris_kasa_id');
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter(d => {
      if (aramaMetni) {
        const q = aramaMetni.toLowerCase();
        if (!((d.giris_kasa || '').toLowerCase().includes(q) || (d.cikis_kasa || '').toLowerCase().includes(q) || (d.aciklama || '').toLowerCase().includes(q))) return false;
      }
      if (!secimeUyuyor(filtreCikisKasaIds, d.cikis_kasa_id)) return false;
      if (!secimeUyuyor(filtreGirisKasaIds, d.giris_kasa_id)) return false;
      if (filtreTutarOp && filtreTutarDeger != null) {
        const tutar = Number(d.tutar || 0);
        const deger = Number(filtreTutarDeger);
        if (filtreTutarOp === 'gt' && !(tutar > deger)) return false;
        if (filtreTutarOp === 'gte' && !(tutar >= deger)) return false;
        if (filtreTutarOp === 'lt' && !(tutar < deger)) return false;
        if (filtreTutarOp === 'lte' && !(tutar <= deger)) return false;
        if (filtreTutarOp === 'eq' && !(tutar === deger)) return false;
      }
      if (filtreTarihBaslangic && dayjs(d.tarih).isBefore(filtreTarihBaslangic, 'day')) return false;
      if (filtreTarihBitis && dayjs(d.tarih).isAfter(filtreTarihBitis, 'day')) return false;
      return true;
    });
  }, [data, aramaMetni, filtreCikisKasaIds, filtreGirisKasaIds, filtreTutarOp, filtreTutarDeger, filtreTarihBaslangic, filtreTarihBitis]);

  const toplamlar = useMemo(() => {
    const t = { adet: filteredData.length, tutar: 0 };
    filteredData.forEach(d => { t.tutar += Number(d.tutar || 0); });
    return t;
  }, [filteredData]);

  const filtreAktifMi = filtreCikisKasaIds.length > 0 || filtreGirisKasaIds.length > 0 || filtreTutarOp || filtreTarihBaslangic || filtreTarihBitis;

  const filtreTemizle = () => {
    setFiltreCikisKasaIds([]); setFiltreGirisKasaIds([]); setFiltreTutarOp(null); setFiltreTutarDeger(null); setFiltreTarihBaslangic(null); setFiltreTarihBitis(null);
  };

  const TUR_CONFIG = {
    transfer: { color: 'blue', label: 'Transfer' },
    giris: { color: 'green', label: 'Giriş Virman' },
    cikis: { color: 'orange', label: 'Çıkış Virman' },
  };

  const columns = [
    { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: isMobile ? 50 : 110, render: (v) => v ? dayjs(v).format(isMobile ? 'DD.MM.YY' : 'DD.MM.YYYY') : '-', sorter: (a, b) => { const d = new Date(a.tarih || 0) - new Date(b.tarih || 0); return d !== 0 ? d : (a.id || 0) - (b.id || 0); }, defaultSortOrder: 'descend' },
    { title: 'Tür', dataIndex: 'tur', key: 'tur', width: isMobile ? 90 : 130,
      render: (v) => { const cfg = TUR_CONFIG[v] || TUR_CONFIG.transfer; return <Tag color={cfg.color}>{cfg.label}</Tag>; },
      sorter: (a, b) => (a.tur || '').localeCompare(b.tur || '') },
    { title: <span style={{ display: 'inline-block', whiteSpace: 'pre-line', lineHeight: 1.2 }}>{isMobile ? 'Çıkış\nKasa' : 'Çıkış Kasa'}</span>, dataIndex: 'cikis_kasa', key: 'cikis_kasa', render: (v) => v || <span style={{ color: '#bbb' }}>—</span>, sorter: (a, b) => (a.cikis_kasa || '').localeCompare(b.cikis_kasa || '', 'tr') },
    { title: <span style={{ display: 'inline-block', whiteSpace: 'pre-line', lineHeight: 1.2 }}>{isMobile ? 'Giriş\nKasa' : 'Giriş Kasa'}</span>, dataIndex: 'giris_kasa', key: 'giris_kasa', render: (v) => v || <span style={{ color: '#bbb' }}>—</span>, sorter: (a, b) => (a.giris_kasa || '').localeCompare(b.giris_kasa || '', 'tr') },
    { title: 'Tutar', dataIndex: 'tutar', key: 'tutar', width: isMobile ? 65 : 120, align: 'right', render: (v) => v != null ? formatPara(v) : '-', sorter: (a, b) => (a.tutar || 0) - (b.tutar || 0) },
    { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama', responsive: ['lg'] },
    {
      title: 'İşlem', key: 'islem', width: isMobile ? 80 : 160,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingRecord(record); setModalOpen(true); }}>{!isMobile && 'Düzenle'}</Button>
          <Popconfirm title="Bu virmanı silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Virman</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 220 }} />
          <Button icon={<FilterOutlined />} onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])} type={filtreAktifMi ? 'primary' : 'default'} ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}>
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingRecord(null); setModalOpen(true); }} title="Yeni Virman">
            {!isMobile && 'Yeni Virman'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Çıkış Kasası</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Çıkış kasası seçin..." options={cikisKasaFilterOptions} value={filtreCikisKasaIds} onChange={setFiltreCikisKasaIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Giriş Kasası</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Giriş kasası seçin..." options={girisKasaFilterOptions} value={filtreGirisKasaIds} onChange={setFiltreGirisKasaIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tutar Filtresi</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreTutarOp} onChange={setFiltreTutarOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Tutar" value={filtreTutarDeger} onChange={setFiltreTutarDeger} min={0} />
              </Space.Compact>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Başlangıç</label>
              <DatePicker style={{ width: '100%' }} placeholder="Başlangıç" value={filtreTarihBaslangic} onChange={setFiltreTarihBaslangic} format="DD.MM.YYYY" />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tarih Bitiş</label>
              <DatePicker style={{ width: '100%' }} placeholder="Bitiş" value={filtreTarihBitis} onChange={setFiltreTarihBitis} format="DD.MM.YYYY" />
            </Col>
          </Row>
        </Card>
      )}]} />

      {(
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={6}><Statistic title="Kayıt Sayısı" value={toplamlar.adet} /></Col>
            <Col xs={12} sm={8} md={6}><Statistic title="Toplam Tutar" value={toplamlar.tutar} precision={2} suffix="₺" /></Col>
          </Row>
        </Card>
      )}

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      <Modal title={editingRecord ? 'Virman Düzenle' : 'Yeni Virman'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }} onOk={() => form.submit()} width={480} okText={editingRecord ? 'Güncelle' : 'Kaydet'}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="tarih" label="Tarih" rules={[{ required: true }]} initialValue={dayjs()}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cikis_kasa_id" label="Çıkış Kasası">
            <Select allowClear showSearch optionFilterProp="label" placeholder="Paranın çıkacağı kasayı seçin" options={kasalar.map(k => ({ value: k.id, label: k.ad }))} />
          </Form.Item>
          <Form.Item name="giris_kasa_id" label="Giriş Kasası">
            <Select allowClear showSearch optionFilterProp="label" placeholder="Paranın gireceği kasayı seçin" options={kasalar.map(k => ({ value: k.id, label: k.ad }))} />
          </Form.Item>
          <Form.Item name="tutar" label="Tutar (₺)" rules={[{ required: true }]}>
            <InputNumber {...paraInputProps} min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
      {/* Tek kasa onay modalı */}
      <Modal
        title={<span><WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />Olağandışı İşlem Onayı</span>}
        open={onayModalOpen}
        onCancel={() => { setOnayModalOpen(false); setBekleyenPayload(null); }}
        okText="Evet, devam et"
        cancelText="Vazgeç"
        okButtonProps={{ danger: true }}
        onOk={() => virmanKaydet(bekleyenPayload)}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Tek taraflı virman işlemi"
          description={
            bekleyenPayload && !bekleyenPayload.giris_kasa_id
              ? `Sadece çıkış kasası seçildi. ${bekleyenPayload.tutar} ₺ çıkış kasasından düşülecek ancak herhangi bir kasaya giriş yapılmayacak.`
              : `Sadece giriş kasası seçildi. ${bekleyenPayload?.tutar} ₺ giriş kasasına eklenecek ancak herhangi bir kasadan çıkış yapılmayacak.`
          }
        />
        <p>Bu olağandışı bir işlemdir. Devam etmek istediğinize emin misiniz?</p>
      </Modal>
    </div>
  );
}

export default Virman;
